<template>
    <div>
        <b-container fluid>
            <div class="my-3 mt-5">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="h3 m-0 mr-3 ml-3">
                        Media
                        <b-btn class="btn btn-success ml-5" @click="openAddMedia" :disabled="!can_create_media">
                            Add Media
                        </b-btn>
                        <span
                            id="number-expired-btn"
                            v-if="expired_media && expired_media.length > 0"
                            class="btn btn-danger ml-3"
                            @click="viewExpiredMedia">
                            {{ expired_media.length }}
                        </span>
                        <b-tooltip target="number-expired-btn" triggers="hover" v-if="expired_media && expired_media.length > 0">
                            {{ expired_media.length }} Media have expired. (Click to view)
                        </b-tooltip>
                    </div>
                </div>
            </div>

            <b-form @submit.prevent="getMedia">
                <div class="d-flex align-items-center mb-3">
                    <div class="flex-grow-1">
                        <b-input-group>
                            <b-form-input
                                ref="search-term"
                                type="search"
                                v-model="media_search"
                                placeholder="Search Media"
                                :disabled="!can_search_media"
                            />
                            <b-input-group-append>
                                <b-btn
                                    variant="outline-secondary"
                                    :disabled="loading_media || !can_search_media"
                                    @click="loadLatestMedia"
                                >
                                    Latest
                                </b-btn>
                                <b-btn
                                    type="submit"
                                    variant="outline-secondary"
                                    :disabled="!can_search_media"
                                >
                                    <font-awesome-icon :icon="['fad', 'search']" />
                                </b-btn>
                            </b-input-group-append>
                            <b-input-group-append>
                                <b-btn
                                    id="media-search-info"
                                    variant="light"
                                    :disabled="!can_search_media"
                                >
                                    <font-awesome-icon :icon="['fad', 'info-circle']" />
                                </b-btn>
                                <b-tooltip
                                    target="media-search-info"
                                    triggers="hover focus"
                                    placement="bottom"
                                >
                                    Use AND, OR, NOT, and quotes for exact phrases. Use * for prefix wildcard
                                    (e.g. educat*). Example: "civil rights" AND education NOT war. Fuzzy search
                                    is on by default.
                                </b-tooltip>
                            </b-input-group-append>
                            <div class="d-flex">
                                <div class="mx-2">
                                    <b-btn
                                        variant="light"
                                        :class="filters_visible ? 'active' : 'collapsed'"
                                        @click="filters_visible = !filters_visible"
                                        :disabled="!can_search_media"
                                    >
                                        <font-awesome-icon :icon="['fad', 'filter']" />
                                    </b-btn>
                                </div>
                            </div>
                        </b-input-group>
                    </div>
                </div>
                <b-collapse
                    id="filters"
                    v-model="filters_visible"
                >
                    <div v-if="filters_visible" class="mb-3">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <div class="row align-items-center">
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.types"
                                            :options="media_types"
                                            multiple
                                            label="name"
                                            track-by="name"
                                            placeholder="Filter By Type"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <language-multiselect
                                            :select_label="select_label"
                                            :deselect_label="deselect_label"
                                            v-model="filters.languages"
                                            :options="available_languages"
                                            @add="addLanguage"
                                            @remove="removeLanguage"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.tags"
                                            :options="tags"
                                            multiple
                                            label="name"
                                            track-by="id"
                                            placeholder="Filter By Tags"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-if="active_filters" class="ml-3">
                                <b-btn variant="danger" @click="clearFilters">
                                    <font-awesome-icon :icon="['fad', 'backspace']" /> Clear All Filters
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </b-collapse>
            </b-form>

            <b-alert
                v-if="showListBanner"
                :variant="listBannerVariant"
                show
                class="mb-3"
            >
                {{ listBannerText }}
            </b-alert>

            <div v-if="loading_media || searching">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
            <!-- filterd_media -->
                <b-table
                    v-if="all_media"
                    :items="pagedMedia"
                    :fields="fields"
                    striped
                    responsive
                    sticky-header
                    outlined
                    no-border-collapse
                    head-variant="light"

                >
                <!-- perPage="10" -->
                    <!-- head -->
                    <template v-slot:head(id)>
                        Media ID
                    </template>

                    <template v-slot:head(title)>
                        Title
                    </template>

                    <template v-slot:head(type)>
                        Media Type
                    </template>

                    <template v-slot:head(language)>
                        Language
                    </template>

                    <template v-slot:head(tags)>
                        Tags
                    </template>
                    <!-- end head -->

                    <!-- rows -->
                    <template #cell(id)="data">
                        {{ data.item.id }}
                    </template>

                    <template #cell(title)="data">
                        <div class="d-flex justify-content-start">
                            <b-link :href="data.item.url" target="_blank" class="mx-3">
                                <b-img-lazy
                                    v-if="data.item.type === 'video' && data.item.splash_url"
                                    :src="data.item.splash_url"
                                    :alt="data.item.alt"
                                    height="auto"
                                    width="100px"
                                />
                                <b-img-lazy
                                    v-else-if="data.item.type === 'audio'"
                                    src="https://cdn.studiesweekly.com/online/resources/pod_media/audio_default.png"
                                    :alt="data.item.alt"
                                    height="auto"
                                    width="100px"
                                />
                                <b-img-lazy
                                    v-else-if="data.item.type === 'image'"
                                    :src="data.item.url"
                                    :alt="data.item.alt"
                                    height="auto"
                                    width="100px"
                                    thumbnail
                                />
                                <b-img-lazy
                                    v-else-if="data.item.type === 'sims'"
                                    :src="data.item.splash_url"
                                    :alt="data.item.alt"
                                    height="auto"
                                    width="100px"
                                    thumbnail
                                />
                                <b-img-lazy
                                    v-else
                                    :src="data.item.url"
                                    :alt="data.item.alt"
                                    height="auto"
                                    width="100px"
                                    thumbnail
                                />
                            </b-link>
                            <div class="d-flex align-items-center">
                                <div class="text-primary cursor-pointer" @click="openEditMediaModal(data.item)"><span v-if="data.item.title">{{ data.item.title }}</span><span v-else class="text-muted">no title</span></div>
                            </div>
                        </div>
                    </template>

                    <template #cell(media_type)="data">
                        {{ data.item.type }}
                    </template>

                    <template #cell(language)="data">
                        <div  class="d-flex align-items-center justify-content-between">
                            <b-link :href="data.item.url" target="_blank" class="d-flex align-items justify-content-center text-uppercase url-link">
                                <div class="mr-1">
                                    <span v-if="data.item.language == 'en' || data.item.language == 'us'">
                                        <flag iso="us" />
                                    </span>
                                    <span v-if="data.item.language == 'es' || data.item.language == 'sp'">
                                        <flag iso="es" />
                                    </span>
                                </div>
                                <div v-if="data.item.language">
                                    {{data.item.language}}
                                </div>
                                <div v-else>
                                    N/A
                                </div>
                            </b-link>
                            <div v-if="data.item.media_group.length > 1" class="mr-4">
                                <font-awesome-icon :id="`link-popover-${data.item.id}`" :icon="['fad', 'link']" class="text-primary" fixed-width />

                                <div>
                                    <b-popover :target="`link-popover-${data.item.id}`" triggers="hover" placement="right" class="mt-2">
                                        <template #title>
                                            Linked Alternate Media
                                        </template>
                                        <div>
                                            <div class="mt-2">
                                                <div class="pedigree_container">
                                                    <b-list-group>
                                                        <b-list-group-item v-for="(item, index) in linkedMedia(data.item)" :key="index">
                                                            <div class="d-flex align-items-center">
                                                                <b-link :href="item.url" target="_blank" class="mr-1">
                                                                    <span v-if="item.language == 'en' || item.language == 'us'">
                                                                        <flag iso="us" />
                                                                    </span>
                                                                    <span v-if="item.language == 'es' || item.language == 'sp'">
                                                                        <flag iso="es" />
                                                                    </span>
                                                                </b-link>
                                                                <div class="text-primary cursor-pointer" @click="openEditMediaModal(item)">
                                                                    {{item.id}}: {{ item.title }}
                                                                </div>
                                                            </div>
                                                        </b-list-group-item>
                                                    </b-list-group>
                                                </div>
                                            </div>
                                        </div>
                                    </b-popover>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #cell(qr_code_url)="data">
                        <b-link
                            v-if="data.item.qr_code_url"
                            :href="data.item.qr_code_url"
                            target="_blank"
                            class="mx-3"
                        >
                            <b-img-lazy
                                :src="data.item.qr_code_url"
                                height="auto"
                                width="100px"
                            />
                        </b-link>
                    </template>

                    <template #cell(expiration_date)="data">
                        <div v-if="data.item.expiration_date" class="badge p-2" :class="data.item.expired != '1' ? 'badge-primary' : 'badge-danger'">
                            {{ new Date(data.item.expiration_date).toLocaleDateString('en-US') }}
                        </div>
                    </template>

                    <template #cell(tags)="data">
                        <b-alert
                            variant="danger"
                            :show="!data.item.tags || data.item.tags && !data.item.tags.length"
                            class="my-2"
                        >
                            0 Tags added
                        </b-alert>
                        <b-badge v-for="(tag, index) in data.item.tags" variant="primary" class="m-1 py-2 px-3">{{ tag.name }}</b-badge>
                    </template>

                    <template #cell(url)="data">
                        <b-btn
                            :variant="copy_to_clipboard_btn_variant(data.item)"
                            :class="{'active': data.item.copying_to_clipboard}"
                            content="Copy URL to Clipboard"
                            v-tippy="{placement: 'top'}"
                            @click="copyToClipboard(data.item)"
                            :disabled="data.item.copying_to_clipboard"
                            class="my-2"
                            size="sm"
                        >
                            <span v-if="data.item.copying_to_clipboard">
                                <font-awesome-icon :icon="['fas', 'check']" size="lg" fixed-width />
                            </span>
                            <span v-else>
                                <font-awesome-icon :icon="['fad', 'clipboard']" size="lg" fixed-width />
                            </span>
                        </b-btn>
                    </template>
                    <!-- end rows -->
                </b-table>

                <div v-if="all_media && all_media.length" class="d-flex align-items-center justify-content-between mt-3">
                    <div class="d-flex align-items-center">
                        <span class="mr-2">Per page</span>
                        <b-form-select
                            v-model="per_page"
                            :options="per_page_options"
                            size="sm"
                        />
                    </div>
                    <b-pagination
                        v-model="current_page"
                        :total-rows="all_media.length"
                        :per-page="per_page"
                        size="sm"
                        align="right"
                    />
                </div>
            </div>
        </b-container>

        <upload-media-modal
            v-model="show_upload_media_modal"
            :value="null"
            @hideUploadMedia="show_upload_media_modal = false"
            @getMedia="getMedia"
        />

        <edit-media-modal
            v-if="editing_media"
            :key="editing_media.id"
            v-model="show_edit_media_modal"
            :value="null"
            :editing_media="editing_media"
            :pedigree_loading="pedigree_loading"
            :editing_media_pedigree_data="editing_media_pedigree_data"
            :alternate_media_prop="alternate_media_prop"
            @hideUploadMedia="closeEditMediaModal"
            @mediaUploadSuccess="showSuccessMessage"
            @getMedia="getUpdatedMedia"
        />

    </div>
</template>

<style lang="scss" scoped>

.url-link {
    color: unset;
}
.url-link:hover {
    text-decoration: none;
}
.media-title {
    max-height: 30px;
}
.cursor-pointer {
    cursor: pointer;
}

</style>

<script>

import UploadMediaModal from '@/components/modals/media/UploadMediaModal'
import EditMediaModal from '@/components/modals/media/EditMediaModal'
import { mapState, mapActions } from 'vuex';
import LanguageMultiselect from '@/components/LanguageMultiselect'
import listBannerMixin from '@/common/list_banner_mixin'

import StandardModal from '@/components/modals/StandardModal'
import copy from 'copy-html-to-clipboard'

export default {
    mixins: [listBannerMixin],
    data: () => ({
        media_search: '',
        all_media: [],
        filters_visible: false,
        filtered_media: [],
        fields: [
            {
                key: 'id',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'title',
                sortable: true,
                thStyle: { width: "30%" },
            },
            {
                key: 'type',
                sortable: true,
                thStyle: { width: "10%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            },
            {
                key: 'language',
                sortable: true,
                thStyle: { width: "5%" },
            },
            {
                key: 'qr_code_url',
                label: 'QR Code',
                sortable: true,
                thStyle: { width: "5%" },
            },
            {
                key: 'expiration_date',
                label: 'Expiration Date',
                sortable: true,
                thStyle: { width: "5%" },
            },
            {
                key: 'tags',
                sortable: true,
                thStyle: { width: "20%" },
            },
            {
                key: 'url',
                label: 'Copy URL',
                sortable: true,
                thStyle: { width: "5%" },
                thClass: 'text-center',
                tdClass: 'text-center',
            }
        ],

        media: {},

        media_types: [
            {name: 'image'},
            {name: 'audio'},
            {name: 'video'},

        ],

        filters: {tags: [], languages: [], types: []},
        filtering: false,
        select_label: '',
        deselect_label: 'remove',
        searched: false,
        searching: false,

        loading_media: false,

        show_upload_media_modal: false,
        show_edit_media_modal: false,

        editing_media: null,
        editing_media_pedigree_data: null,
        alternate_media_prop: null,
        pedigree_loading: false,
        expired_media: [],
        active_media_list: 'latest',
        current_page: 1,
        per_page: 25,
        per_page_options: [10, 25, 50, 100],
    }),
    props: [],
    components: {
        StandardModal,
        UploadMediaModal,
        EditMediaModal,
        LanguageMultiselect
    },
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user,
            tags: state => state.tags.tags,
        }),
        can_create_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_media')
        },
        can_search_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'search_media')
        },
        active_filters () {
            let active_filters = false

            if (
                this.filters &&
                _.some(this.filters)
            ) {
                active_filters = true
            }

            return active_filters
        },
        pagedMedia () {
            if (!this.all_media || !this.all_media.length) {
                return []
            }
            const start = (this.current_page - 1) * this.per_page
            const end = start + this.per_page
            return this.all_media.slice(start, end)
        },
        available_languages () {
            let languages = []

            _.forEach(this.all_media, media => {
                languages.push({
                    language: media.language
                })
            })

            languages = _.orderBy(languages, 'language')
            languages = _.uniqBy(languages, 'language')

            return languages
        },
        bannerMode() {
            return this.active_media_list
        },
        bannerSearchTerm() {
            return this.media_search
        },
        bannerItemLabel() {
            return 'media'
        },
    },
    watch: {
        per_page () {
            this.current_page = 1
        },
        all_media (media) {
            const totalPages = Math.max(1, Math.ceil((media || []).length / this.per_page))
            if (this.current_page > totalPages) {
                this.current_page = 1
            }
        },
        filters: {
            deep: true,
            handler () {
                this.handleFilterChange()
            },
        },
    },
    methods: {
        buildSearchParams () {
            return {
                tags: this.filters.tags.map(tag => tag.name),
                languages: this.filters.languages.map(language => language.language),
                types: this.filters.types.map(type => type.name),
                tag_mode: 'or',
            }
        },
        copy_to_clipboard_btn_variant (media) {
            let variant = "light"

            if (media.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        async getMedia() {
            this.active_media_list = 'searched';
            this.loading_media = true
            try {
                const sanitizedSearch = encodeURIComponent(this.media_search)
                const response = await this.axios.get(`/api/media/search_media/${sanitizedSearch}`, {
                    params: this.buildSearchParams(),
                })
                if (response.data) {
                    this.all_media = response.data;
                }
                this.current_page = 1
                this.loading_media = false
            } catch (e) {
                this.loading_media = false
                this.searching = false
            }

        },
        handleFilterChange () {
            this.current_page = 1

            if (this.active_media_list !== 'searched' || !this.media_search || !this.media_search.trim()) {
                return
            }

            this.getMedia()
        },
        async loadLatestMedia() {
            this.active_media_list = 'latest';
            this.loading_media = true;

            try {
                const response = await this.axios.get('/api/media/latest')
                if (response.data) {
                    this.all_media = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get latest media: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_media = false;
            }
        },
        getUpdatedMedia(){
            if(this.active_media_list == 'searched'){
                this.getMedia();
            }
            else if(this.active_media_list == 'latest'){
                this.loadLatestMedia();
            }
            else{
                this.getExpiredMedia(true);
            }
        },
        viewExpiredMedia(){
            this.active_media_list = 'expired';
            this.all_media = this.expired_media;
        },
        linkedMedia(media) {
            const { id, media_group } = media;

            return media_group.filter((media)=> media.id !== id)
        },

        clearFilters () {
            this.filtering = false;
            this.filters = {tags: [], languages: [], types: []}
        },
        openAddMedia(){
            this.show_upload_media_modal = true;
        },
        closeUploadMediaModal(){
            this.show_add_public_media_modal = true;
            this.show_upload_media_modal = false;
        },
        openEditMediaModal(edit_media){
            this.$store.commit('setSelectedMedia', Object.assign({}, edit_media));
            this.editing_media = Object.assign({}, edit_media);
            if (Array.isArray(this.editing_media.merlin_ids)) {
                this.editing_media.merlin_ids = this.editing_media.merlin_ids.join(', ')
            }
            this.show_edit_media_modal = true;
        },
        closeEditMediaModal(){
            this.editing_media = null;
            this.show_edit_media_modal = false;
        },
        showSuccessMessage() {
            this.$bvToast.toast('Media was successfully updated', {
                title: 'Success',
                variant: 'success',
                solid: false,
                autoHideDelay: 3000,
                appendToast: true
            })
        },
        addLanguage (new_language) {
            this.filters.languages.push(new_language)
        },
        removeLanguage (language) {
            let index = _.findIndex(this.filters.languages, {language: language.language})
            this.filters.languages.splice(index, 1)
        },
        copyToClipboard (media) {
            let media_url = media.url;
            if (!(media_url.startsWith("http:") || media_url.startsWith("https:"))) {
                media_url = `https:${media_url}`;
            }
            copy(media_url, {
                asHtml: false
            })
            let index = this.all_media.findIndex(obj => obj.id === media.id)
            this.all_media[index].copying_to_clipboard = true

            setTimeout(() => {
                this.all_media[index].copying_to_clipboard = false
            }, 1500)
        },
        async getExpiredMedia(refresh_list=false) {
            try {
                const response = await this.axios.get(`/api/media/get_expired_media`)
                if (response.data) {
                    this.expired_media = response.data;
                    if(refresh_list){
                        this.all_media = this.expired_media;
                    }
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get expired media: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }

        },
    },
    mounted () {
        this.$store.dispatch('getTags');
        this.getExpiredMedia();
        this.loadLatestMedia();
    }
}
</script>
